import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import classNames from "classnames";
import { BandBadge, TablePager } from "../../primitives";
import { BlockBaseProps } from "../../types";
import styles from "../table.module.scss";
import PaymentRecordsTableSkeleton from "./Skeleton";

export interface PaymentRow {
  id: string;
  /** Already formatted, without the currency prefix. */
  amount: string;
  method: string;
  instalment: string;
  status: string;
  due: string;
  purchased: string;
  /** Marks a receipt that could not be verified. */
  flagged?: boolean;
  flagNote?: string;
}

interface PaymentRecordsTableProps extends BlockBaseProps {
  payments: PaymentRow[];
  onViewReceipt?: (id: string) => void;
  /** Opens the rejection reason. Only flagged rows offer it. */
  onViewReason?: (id: string) => void;
  title?: string;
  currency?: string;
  /** Server-side paging. Omit `total` to render the table without a pager. */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  /** Shown in place of the rows when the school has no payments. */
  emptyLabel?: string;
}

const COLUMNS = [
  "Transaction ID",
  "Amount",
  "Method",
  "Instalment",
  "Status",
  "Due date",
  "Purchased date",
  "Receipt",
];

/** Every payment recorded against the school's plans. */
function PaymentRecordsTable({
  payments,
  onViewReceipt,
  onViewReason,
  title = "Payment records",
  currency = "Rs.",
  page = 0,
  pageSize = 10,
  total,
  onPageChange,
  emptyLabel = "No payments recorded for this plan.",
  loading,
  className,
}: PaymentRecordsTableProps) {
  if (loading) {
    return (
      <PaymentRecordsTableSkeleton
        count={payments.length || 3}
        className={className}
      />
    );
  }

  return (
    <div className={classNames(styles.card, className)}>
      <div className={styles.heading}>{title}</div>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} className={styles.th}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!payments.length && (
              <tr>
                <td className={`${styles.td} ${styles.empty}`} colSpan={COLUMNS.length}>
                  {emptyLabel}
                </td>
              </tr>
            )}
            {payments.map((payment, index) => (
              <tr key={`${payment.id}-${index}`}>
                <td className={`${styles.td} ${styles.strong}`}>{payment.id}</td>
                <td className={`${styles.td} ${styles.nowrap}`}>
                  {`${currency} ${payment.amount}`}
                </td>
                <td className={styles.td}>{payment.method}</td>
                <td className={styles.td}>{payment.instalment}</td>
                <td className={styles.td}>
                  <BandBadge band={payment.status} status />
                </td>
                <td
                  className={classNames(
                    styles.td,
                    styles.nowrap,
                    payment.status === "Due" && styles.dueUrgent
                  )}
                >
                  {payment.due}
                </td>
                <td className={`${styles.td} ${styles.nowrap}`}>
                  {payment.purchased}
                </td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      aria-label="View receipt"
                      className={styles.iconBtn}
                      onClick={() => onViewReceipt?.(payment.id)}
                    >
                      <VisibilityIcon />
                    </button>
                    {payment.flagged && (
                      <button
                        type="button"
                        aria-label="View rejection reason"
                        title={payment.flagNote}
                        className={styles.flag}
                        onClick={() => onViewReason?.(payment.id)}
                      >
                        <ErrorOutlineIcon />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total != null && total > pageSize && onPageChange && (
        <TablePager
          page={page}
          pageSize={pageSize}
          total={total}
          onChange={onPageChange}
        />
      )}
    </div>
  );
}

export default PaymentRecordsTable;
